/**
 * Editor Store - Manages editor state with local changes and server data
 *
 * Tracks both server state (source of truth) and local pending changes
 */

import { writable, derived, get } from 'svelte/store';
import type {
  Category,
  WordBank,
  PromptFile,
  FrameworkFile,
  EditorDataOverview,
} from '@blather/shared';
import { editorApiService } from '../services/editorApiService';
import { undoStore } from './undoStore';

export type EditorMode = 'wordbanks' | 'prompts' | 'frameworks' | null;

interface ConflictInfo {
  type: 'wordbanks' | 'prompts' | 'frameworks';
  key: string;
  serverVersion: any;
  localVersion: any;
}

interface EditorState {
  // Server data (source of truth)
  serverData: {
    wordbanks: Map<string, WordBank>;
    prompts: Map<Category, PromptFile>;
    frameworks: Map<Category, FrameworkFile>;
  };

  // Local pending changes (not yet saved)
  localData: {
    wordbanks: Map<string, WordBank>;
    prompts: Map<Category, PromptFile>;
    frameworks: Map<Category, FrameworkFile>;
  };

  // UI state
  currentMode: EditorMode;
  selectedItem: string | null; // Which wordbank/category is selected
  availableItems: string[]; // List of available items for current mode

  // Conflict detection
  conflicts: ConflictInfo[] | null;
  autoMergedItems: string[]; // Track items that were auto-merged

  // Flags
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  error: string | null;
}

function createInitialState(): EditorState {
  return {
    serverData: {
      wordbanks: new Map(),
      prompts: new Map(),
      frameworks: new Map(),
    },
    localData: {
      wordbanks: new Map(),
      prompts: new Map(),
      frameworks: new Map(),
    },
    currentMode: null,
    selectedItem: null,
    availableItems: [],
    conflicts: null,
    autoMergedItems: [],
    hasUnsavedChanges: false,
    isSaving: false,
    isLoading: false,
    error: null,
  };
}

function createEditorStore() {
  const { subscribe, set, update } = writable<EditorState>(createInitialState());

  return {
    subscribe,

    /**
     * Load initial data overview
     */
    async loadOverview() {
      update(state => ({ ...state, isLoading: true, error: null }));

      try {
        const overview: EditorDataOverview = await editorApiService.getOverview();

        update(state => ({
          ...state,
          isLoading: false,
        }));

        return overview;
      } catch (error) {
        update(state => ({
          ...state,
          isLoading: false,
          error: error instanceof Error ? error.message : String(error),
        }));
        throw error;
      }
    },

    /**
     * Set editor mode and load available items
     */
    async setMode(mode: EditorMode, overview: EditorDataOverview) {
      let availableItems: string[] = [];

      if (mode === 'wordbanks') {
        availableItems = overview.wordbanks;
      } else if (mode === 'prompts') {
        availableItems = overview.promptCategories;
      } else if (mode === 'frameworks') {
        availableItems = overview.frameworkCategories;
      }

      update(state => ({
        ...state,
        currentMode: mode,
        availableItems,
        selectedItem: null,
      }));
    },

    /**
     * Select an item (wordbank/category) and load its data
     * Always fetches fresh data from server to avoid stale data issues
     */
    async selectItem(item: string) {
      const state = get({ subscribe });

      if (!state.currentMode) {
        throw new Error('No mode selected');
      }

      update(s => ({ ...s, isLoading: true, error: null }));

      try {
        if (state.currentMode === 'wordbanks') {
          const data = await editorApiService.getWordBank(item);
          update(s => ({
            ...s,
            selectedItem: item,
            isLoading: false,
          }));

          // Always update with fresh data from server
          update(s => {
            s.serverData.wordbanks.set(item, data);
            // Only update local data if no unsaved changes for this item
            if (!s.hasUnsavedChanges || !s.localData.wordbanks.has(item)) {
              s.localData.wordbanks.set(item, JSON.parse(JSON.stringify(data)));
            }
            return s;
          });
        } else if (state.currentMode === 'prompts') {
          const data = await editorApiService.getPrompts(item as Category);
          update(s => ({
            ...s,
            selectedItem: item,
            isLoading: false,
          }));

          update(s => {
            s.serverData.prompts.set(item as Category, data);
            // Only update local data if no unsaved changes for this item
            if (!s.hasUnsavedChanges || !s.localData.prompts.has(item as Category)) {
              s.localData.prompts.set(item as Category, JSON.parse(JSON.stringify(data)));
            }
            return s;
          });
        } else if (state.currentMode === 'frameworks') {
          const data = await editorApiService.getFrameworks(item as Category);
          update(s => ({
            ...s,
            selectedItem: item,
            isLoading: false,
          }));

          update(s => {
            s.serverData.frameworks.set(item as Category, data);
            // Only update local data if no unsaved changes for this item
            if (!s.hasUnsavedChanges || !s.localData.frameworks.has(item as Category)) {
              s.localData.frameworks.set(item as Category, JSON.parse(JSON.stringify(data)));
            }
            return s;
          });
        }
      } catch (error) {
        update(s => ({
          ...s,
          isLoading: false,
          error: error instanceof Error ? error.message : String(error),
        }));
        throw error;
      }
    },

    /**
     * Update local data (not saved to server yet)
     */
    updateLocalData(type: 'wordbanks' | 'prompts' | 'frameworks', key: string, data: any) {
      update(state => {
        if (type === 'wordbanks') {
          state.localData.wordbanks.set(key, data);
        } else if (type === 'prompts') {
          state.localData.prompts.set(key as Category, data);
        } else if (type === 'frameworks') {
          state.localData.frameworks.set(key as Category, data);
        }

        // Mark as having unsaved changes
        state.hasUnsavedChanges = true;
        return state;
      });
    },

    /**
     * Try to auto-merge wordbank changes if only additions were made
     * Returns merged wordbank if successful, null if merge not possible
     */
    canAutoMergeWordBank(base: WordBank, server: WordBank, local: WordBank): WordBank | null {
      // Check if only additions (no deletions or modifications to existing words)
      const baseWords = new Set(base.words);
      const serverWords = new Set(server.words);
      const localWords = new Set(local.words);

      // Check if all base words still exist in both server and local
      const allBaseInServer = base.words.every(w => serverWords.has(w));
      const allBaseInLocal = base.words.every(w => localWords.has(w));

      if (!allBaseInServer || !allBaseInLocal) {
        // Someone removed or modified words - can't auto-merge
        return null;
      }

      // Check if other properties are unchanged
      if (base.slotType !== server.slotType || base.slotType !== local.slotType ||
          base.defaultWord !== server.defaultWord || base.defaultWord !== local.defaultWord ||
          base.maxPerSlot !== server.maxPerSlot || base.maxPerSlot !== local.maxPerSlot ||
          base.numWordsShown !== server.numWordsShown || base.numWordsShown !== local.numWordsShown) {
        // Properties changed - can't auto-merge
        return null;
      }

      // Auto-merge: combine all unique words
      const mergedWords = Array.from(new Set([...server.words, ...local.words]));

      console.log(`[Auto-Merge] Successfully merged wordbank:`, {
        base: base.words.length,
        server: server.words.length,
        local: local.words.length,
        merged: mergedWords.length,
      });

      return {
        ...base,
        words: mergedWords,
      };
    },

    /**
     * Try to auto-merge prompt changes if only additions were made
     */
    canAutoMergePrompts(base: PromptFile, server: PromptFile, local: PromptFile): PromptFile | null {
      // Create maps by word for easy lookup
      const baseMap = new Map(base.prompts.map(p => [p.word, p]));
      const serverMap = new Map(server.prompts.map(p => [p.word, p]));
      const localMap = new Map(local.prompts.map(p => [p.word, p]));

      // Check if all base prompts still exist unchanged in both server and local
      for (const prompt of base.prompts) {
        const serverPrompt = serverMap.get(prompt.word);
        const localPrompt = localMap.get(prompt.word);

        if (!serverPrompt || !localPrompt) {
          return null; // Prompt was removed
        }

        // Check if modified
        if (JSON.stringify(serverPrompt) !== JSON.stringify(prompt) ||
            JSON.stringify(localPrompt) !== JSON.stringify(prompt)) {
          return null; // Prompt was modified
        }
      }

      // Check category unchanged
      if (base.category !== server.category || base.category !== local.category) {
        return null;
      }

      // Auto-merge: combine all unique prompts (by word)
      const mergedMap = new Map([...serverMap, ...localMap]);
      const mergedPrompts = Array.from(mergedMap.values());

      console.log(`[Auto-Merge] Successfully merged prompts:`, {
        base: base.prompts.length,
        server: server.prompts.length,
        local: local.prompts.length,
        merged: mergedPrompts.length,
      });

      return {
        category: base.category,
        prompts: mergedPrompts,
      };
    },

    /**
     * Try to auto-merge framework changes if only additions were made
     */
    canAutoMergeFrameworks(base: FrameworkFile, server: FrameworkFile, local: FrameworkFile): FrameworkFile | null {
      // Create maps by id for easy lookup
      const baseMap = new Map(base.frameworks.map(f => [f.id, f]));
      const serverMap = new Map(server.frameworks.map(f => [f.id, f]));
      const localMap = new Map(local.frameworks.map(f => [f.id, f]));

      // Check if all base frameworks still exist unchanged in both server and local
      for (const framework of base.frameworks) {
        const serverFramework = serverMap.get(framework.id);
        const localFramework = localMap.get(framework.id);

        if (!serverFramework || !localFramework) {
          return null; // Framework was removed
        }

        // Check if modified
        if (JSON.stringify(serverFramework) !== JSON.stringify(framework) ||
            JSON.stringify(localFramework) !== JSON.stringify(framework)) {
          return null; // Framework was modified
        }
      }

      // Check category unchanged
      if (base.category !== server.category || base.category !== local.category) {
        return null;
      }

      // Auto-merge: combine all unique frameworks (by id)
      const mergedMap = new Map([...serverMap, ...localMap]);
      const mergedFrameworks = Array.from(mergedMap.values());

      console.log(`[Auto-Merge] Successfully merged frameworks:`, {
        base: base.frameworks.length,
        server: server.frameworks.length,
        local: local.frameworks.length,
        merged: mergedFrameworks.length,
      });

      return {
        category: base.category,
        frameworks: mergedFrameworks,
      };
    },

    /**
     * Detect conflicts by fetching latest data from server
     * Returns array of conflicts if any exist
     * Automatically merges changes when possible (both clients only added items)
     */
    async detectConflicts(): Promise<ConflictInfo[]> {
      const state = get({ subscribe });
      const conflicts: ConflictInfo[] = [];

      // Clear auto-merged items from previous check
      update(s => {
        s.autoMergedItems = [];
        return s;
      });

      console.log('[Conflict Detection] Starting conflict detection...');
      console.log('[Conflict Detection] Current state:', {
        localWordbanks: Array.from(state.localData.wordbanks.keys()),
        serverWordbanks: Array.from(state.serverData.wordbanks.keys()),
      });

      // Fetch latest data for all items with pending changes
      const fetchPromises: Promise<void>[] = [];

      // Check wordbanks
      state.localData.wordbanks.forEach((localBank, key) => {
        const serverBank = state.serverData.wordbanks.get(key);
        console.log(`[Conflict Detection] Checking wordbank: ${key}`);
        console.log(`  - Has serverBank: ${!!serverBank}`);
        console.log(`  - Local words count: ${localBank.words?.length || 0}`);
        console.log(`  - Server words count: ${serverBank?.words?.length || 0}`);

        if (!serverBank || JSON.stringify(localBank) !== JSON.stringify(serverBank)) {
          console.log(`  - Changes detected, fetching latest from server...`);
          fetchPromises.push(
            editorApiService.getWordBank(key).then(latestData => {
              console.log(`  - Fetched latest data, words count: ${latestData.words?.length || 0}`);
              // Compare latest server data with what we think is on server
              const latestStr = JSON.stringify(latestData);
              const serverStr = JSON.stringify(serverBank);
              const isDifferent = latestStr !== serverStr;

              console.log(`  - Latest different from serverData: ${isDifferent}`);

              if (isDifferent) {
                // Try auto-merge
                const merged = this.canAutoMergeWordBank(serverBank, latestData, localBank);

                if (merged) {
                  console.log(`  - AUTO-MERGED for ${key}! No conflict needed.`);
                  // Update both serverData and localData with merged version
                  update(s => {
                    s.serverData.wordbanks.set(key, latestData); // Update base to latest
                    s.localData.wordbanks.set(key, merged); // Update local to merged
                    s.autoMergedItems.push(`wordbank:${key}`);
                    return s;
                  });
                } else {
                  console.log(`  - CONFLICT DETECTED for ${key}! Cannot auto-merge.`);
                  conflicts.push({
                    type: 'wordbanks',
                    key,
                    serverVersion: latestData,
                    localVersion: localBank,
                  });
                }
              }
            })
          );
        } else {
          console.log(`  - No changes detected`);
        }
      });

      // Check prompts
      state.localData.prompts.forEach((localPrompts, key) => {
        const serverPrompts = state.serverData.prompts.get(key);
        if (!serverPrompts || JSON.stringify(localPrompts) !== JSON.stringify(serverPrompts)) {
          fetchPromises.push(
            editorApiService.getPrompts(key).then(latestData => {
              if (JSON.stringify(latestData) !== JSON.stringify(serverPrompts)) {
                // Try auto-merge
                const merged = this.canAutoMergePrompts(serverPrompts, latestData, localPrompts);

                if (merged) {
                  console.log(`[Auto-Merge] Prompts for ${key} auto-merged successfully`);
                  update(s => {
                    s.serverData.prompts.set(key as Category, latestData);
                    s.localData.prompts.set(key as Category, merged);
                    s.autoMergedItems.push(`prompts:${key}`);
                    return s;
                  });
                } else {
                  console.log(`[Conflict] Prompts for ${key} cannot auto-merge`);
                  conflicts.push({
                    type: 'prompts',
                    key,
                    serverVersion: latestData,
                    localVersion: localPrompts,
                  });
                }
              }
            })
          );
        }
      });

      // Check frameworks
      state.localData.frameworks.forEach((localFrameworks, key) => {
        const serverFrameworks = state.serverData.frameworks.get(key);
        if (!serverFrameworks || JSON.stringify(localFrameworks) !== JSON.stringify(serverFrameworks)) {
          fetchPromises.push(
            editorApiService.getFrameworks(key).then(latestData => {
              if (JSON.stringify(latestData) !== JSON.stringify(serverFrameworks)) {
                // Try auto-merge
                const merged = this.canAutoMergeFrameworks(serverFrameworks, latestData, localFrameworks);

                if (merged) {
                  console.log(`[Auto-Merge] Frameworks for ${key} auto-merged successfully`);
                  update(s => {
                    s.serverData.frameworks.set(key as Category, latestData);
                    s.localData.frameworks.set(key as Category, merged);
                    s.autoMergedItems.push(`frameworks:${key}`);
                    return s;
                  });
                } else {
                  console.log(`[Conflict] Frameworks for ${key} cannot auto-merge`);
                  conflicts.push({
                    type: 'frameworks',
                    key,
                    serverVersion: latestData,
                    localVersion: localFrameworks,
                  });
                }
              }
            })
          );
        }
      });

      await Promise.all(fetchPromises);

      console.log(`[Conflict Detection] Completed. Found ${conflicts.length} conflict(s):`, conflicts);
      return conflicts;
    },

    /**
     * Save all changes to server
     * First checks for conflicts with other clients
     */
    async saveChanges() {
      const state = get({ subscribe });

      update(s => ({ ...s, isSaving: true, error: null, conflicts: null }));

      try {
        // First, check for conflicts with other clients
        const conflicts = await this.detectConflicts();

        if (conflicts.length > 0) {
          // Conflicts detected - set them in state and abort save
          update(s => ({
            ...s,
            isSaving: false,
            conflicts,
            error: 'Conflicts detected. Please review and resolve before saving.',
          }));
          return;
        }

        const promises: Promise<any>[] = [];

        // Save modified wordbanks
        state.localData.wordbanks.forEach((localBank, key) => {
          const serverBank = state.serverData.wordbanks.get(key);
          if (!serverBank || JSON.stringify(localBank) !== JSON.stringify(serverBank)) {
            promises.push(
              editorApiService.updateWordBank(key, localBank).then(response => {
                if (!response.success) {
                  throw new Error(response.errors?.[0]?.message || 'Failed to save word bank');
                }
                return { type: 'wordbank', key, data: response.data };
              })
            );
          }
        });

        // Save modified prompts
        state.localData.prompts.forEach((localPrompts, key) => {
          const serverPrompts = state.serverData.prompts.get(key);
          if (!serverPrompts || JSON.stringify(localPrompts) !== JSON.stringify(serverPrompts)) {
            promises.push(
              editorApiService.updatePrompts(key, localPrompts).then(response => {
                if (!response.success) {
                  throw new Error(response.errors?.[0]?.message || 'Failed to save prompts');
                }
                return { type: 'prompts', key, data: response.data };
              })
            );
          }
        });

        // Save modified frameworks
        state.localData.frameworks.forEach((localFrameworks, key) => {
          const serverFrameworks = state.serverData.frameworks.get(key);
          if (!serverFrameworks || JSON.stringify(localFrameworks) !== JSON.stringify(serverFrameworks)) {
            promises.push(
              editorApiService.updateFrameworks(key, localFrameworks).then(response => {
                if (!response.success) {
                  throw new Error(response.errors?.[0]?.message || 'Failed to save frameworks');
                }
                return { type: 'frameworks', key, data: response.data };
              })
            );
          }
        });

        const results = await Promise.all(promises);

        // Update server data with saved results
        update(s => {
          results.forEach(result => {
            if (result.type === 'wordbank') {
              s.serverData.wordbanks.set(result.key, result.data);
            } else if (result.type === 'prompts') {
              s.serverData.prompts.set(result.key as Category, result.data);
            } else if (result.type === 'frameworks') {
              s.serverData.frameworks.set(result.key as Category, result.data);
            }
          });

          // Sync local with server (discard pending changes)
          s.localData = {
            wordbanks: new Map(s.serverData.wordbanks),
            prompts: new Map(s.serverData.prompts),
            frameworks: new Map(s.serverData.frameworks),
          };

          s.hasUnsavedChanges = false;
          s.isSaving = false;

          return s;
        });

        // Reload server cache
        await editorApiService.reloadCache();

        // Clear undo history after successful save
        undoStore.clearHistory();
      } catch (error) {
        update(s => ({
          ...s,
          isSaving: false,
          error: error instanceof Error ? error.message : String(error),
        }));
        throw error;
      }
    },

    /**
     * Discard all local changes
     */
    discardChanges() {
      update(state => {
        // Reset local data to match server data
        state.localData = {
          wordbanks: new Map(state.serverData.wordbanks),
          prompts: new Map(state.serverData.prompts),
          frameworks: new Map(state.serverData.frameworks),
        };
        state.hasUnsavedChanges = false;
        return state;
      });

      undoStore.clearHistory();
    },

    /**
     * Clear error message
     */
    clearError() {
      update(state => ({ ...state, error: null }));
    },

    /**
     * Reset store to initial state
     */
    reset() {
      set(createInitialState());
      undoStore.clearHistory();
    },

    /**
     * Resolve conflict by using local version (overwrite server)
     */
    resolveConflictUseLocal(conflictIndex: number) {
      update(state => {
        if (!state.conflicts || conflictIndex >= state.conflicts.length) {
          return state;
        }

        const conflict = state.conflicts[conflictIndex];

        // Update serverData to match the server's actual current state
        if (conflict.type === 'wordbanks') {
          state.serverData.wordbanks.set(conflict.key, conflict.serverVersion);
        } else if (conflict.type === 'prompts') {
          state.serverData.prompts.set(conflict.key as Category, conflict.serverVersion);
        } else if (conflict.type === 'frameworks') {
          state.serverData.frameworks.set(conflict.key as Category, conflict.serverVersion);
        }

        // Keep local version - user wants to overwrite
        // Remove this conflict from the list
        state.conflicts = state.conflicts.filter((_, i) => i !== conflictIndex);

        // If no more conflicts, clear the conflicts array
        if (state.conflicts.length === 0) {
          state.conflicts = null;
          state.error = null;
        }

        return state;
      });
    },

    /**
     * Resolve conflict by using server version (discard local changes)
     */
    resolveConflictUseServer(conflictIndex: number) {
      update(state => {
        if (!state.conflicts || conflictIndex >= state.conflicts.length) {
          return state;
        }

        const conflict = state.conflicts[conflictIndex];

        // Update both server and local data to match server's current state
        if (conflict.type === 'wordbanks') {
          state.serverData.wordbanks.set(conflict.key, conflict.serverVersion);
          state.localData.wordbanks.set(conflict.key, JSON.parse(JSON.stringify(conflict.serverVersion)));
        } else if (conflict.type === 'prompts') {
          state.serverData.prompts.set(conflict.key as Category, conflict.serverVersion);
          state.localData.prompts.set(conflict.key as Category, JSON.parse(JSON.stringify(conflict.serverVersion)));
        } else if (conflict.type === 'frameworks') {
          state.serverData.frameworks.set(conflict.key as Category, conflict.serverVersion);
          state.localData.frameworks.set(conflict.key as Category, JSON.parse(JSON.stringify(conflict.serverVersion)));
        }

        // Remove this conflict from the list
        state.conflicts = state.conflicts.filter((_, i) => i !== conflictIndex);

        // If no more conflicts, clear the conflicts array
        if (state.conflicts.length === 0) {
          state.conflicts = null;
          state.error = null;
          // Check if there are any remaining unsaved changes
          state.hasUnsavedChanges = this.hasAnyUnsavedChanges(state);
        }

        return state;
      });
    },

    /**
     * Clear all conflicts without resolving
     */
    clearConflicts() {
      update(state => ({
        ...state,
        conflicts: null,
        error: state.error?.includes('Conflicts detected') ? null : state.error,
      }));
    },

    /**
     * Helper to check if there are any unsaved changes
     */
    hasAnyUnsavedChanges(state: EditorState): boolean {
      // Check wordbanks
      for (const [key, localBank] of state.localData.wordbanks) {
        const serverBank = state.serverData.wordbanks.get(key);
        if (!serverBank || JSON.stringify(localBank) !== JSON.stringify(serverBank)) {
          return true;
        }
      }

      // Check prompts
      for (const [key, localPrompts] of state.localData.prompts) {
        const serverPrompts = state.serverData.prompts.get(key);
        if (!serverPrompts || JSON.stringify(localPrompts) !== JSON.stringify(serverPrompts)) {
          return true;
        }
      }

      // Check frameworks
      for (const [key, localFrameworks] of state.localData.frameworks) {
        const serverFrameworks = state.serverData.frameworks.get(key);
        if (!serverFrameworks || JSON.stringify(localFrameworks) !== JSON.stringify(serverFrameworks)) {
          return true;
        }
      }

      return false;
    },
  };
}

export const editorStore = createEditorStore();

// Derived store for current data (local if editing, otherwise server)
export const currentData = derived(editorStore, $editor => {
  if (!$editor.currentMode || !$editor.selectedItem) {
    return null;
  }

  if ($editor.currentMode === 'wordbanks') {
    return $editor.localData.wordbanks.get($editor.selectedItem) || null;
  } else if ($editor.currentMode === 'prompts') {
    return $editor.localData.prompts.get($editor.selectedItem as Category) || null;
  } else if ($editor.currentMode === 'frameworks') {
    return $editor.localData.frameworks.get($editor.selectedItem as Category) || null;
  }

  return null;
});
